import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { ArrowDown } from "lucide-react";
import { MaskLine, Label } from "./Motion";
import { scrollToId } from "../../lib/api";

const HERO_IMG =
  "https://images.unsplash.com/photo-1522743791393-522312deeebf?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzV8MHwxfHNlYXJjaHw0fHxtb2Rlcm4lMjBtaW5pbWFsJTIwYXJjaGl0ZWN0dXJlJTIwY2FsbXxlbnwwfHx8fDE3ODU2MDQ3MzF8MA&ixlib=rb-4.1.0&q=85";

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "-40%"]);
  const overlayOpacity = useTransform(scrollYProgress, [0, 1], [0.82, 0.94]);

  return (
    <section id="hero" ref={ref} data-testid="hero-section" className="relative min-h-screen overflow-hidden bg-[#eae7de]">
      {/* Parallax background image */}
      <motion.div style={{ y: imgY, scale: imgScale }} className="absolute inset-0 -z-20">
        <img src={HERO_IMG} alt="Structural monolith" className="h-full w-full object-cover grayscale opacity-25" />
      </motion.div>
      <motion.div style={{ opacity: overlayOpacity }} className="absolute inset-0 -z-10 bg-[#eae7de]" />
      <div className="absolute inset-0 -z-10 spotlight" />
      <div className="absolute inset-0 -z-10 grid-lines opacity-40" />

      <motion.div
        style={{ y: textY }}
        className="max-w-[1400px] mx-auto px-6 lg:px-10 min-h-screen flex flex-col justify-center pt-24"
      >
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4, duration: 1 }}
          className="mb-8 flex items-center gap-4"
        >
          <span className="h-px w-12 bg-[#c9a961]" />
          <Label>Understand. Improve. Elevate.</Label>
        </motion.div>

        <h1 className="font-head text-4xl sm:text-5xl lg:text-8xl font-light tracking-tighter leading-[0.95] max-w-6xl text-[#1e3d3a]">
          <MaskLine delay={0.35}>See What's Holding</MaskLine>
          <MaskLine delay={0.5} className="text-black/40">Your Business Back.</MaskLine>
          <MaskLine delay={0.65}>
            Then Elevate It.
          </MaskLine>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1, duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          className="mt-10 max-w-xl text-base lg:text-lg leading-relaxed text-black/65"
        >
          We use business intelligence, strategy, AI, and hands-on consulting to uncover
          inefficiencies, identify opportunities, and help businesses improve what matters most.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.15, duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          className="mt-12 flex flex-wrap items-center gap-4"
        >
          <button
            data-testid="hero-predict-btn"
            onClick={() => scrollToId("predictor")}
            className="rounded-full bg-[#1e3d3a] text-white px-7 py-4 text-sm font-semibold hover:bg-[#c9a961] hover:text-black transition-colors duration-300"
          >
            Run a Free Assessment
          </button>
          <button
            data-testid="hero-pricing-btn"
            onClick={() => scrollToId("pricing")}
            className="rounded-full border border-black/20 px-7 py-4 text-sm font-medium text-[#1e3d3a]/80 hover:text-[#1e3d3a] hover:border-black/50 transition-colors duration-300"
          >
            View Elevares Blueprint
          </button>
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6, duration: 1 }}
        className="absolute bottom-8 left-6 lg:left-10 flex items-center gap-3 text-black/45"
      >
        <ArrowDown className="h-4 w-4 animate-bounce" />
        <span className="text-xs uppercase tracking-[0.25em]">Scroll</span>
      </motion.div>
    </section>
  );
}
